import type { ScaleLinear } from 'd3-scale'
import { stack, stackOffsetDiverging } from 'd3-shape'
import { useCallback, useMemo } from 'react'

import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'

type UseSegmentClickDetectionParams = {
  data: AreaGraphElement[]
  layerKeys: string[]
}

type ClickResult = {
  layerKey: string
} | null

/**
 * Computes stacked layout and resolves which layer was clicked
 * at a given (hourIndex, relativeY) position.
 *
 * The valueScale is passed at call time (not initialization) because
 * it comes from AreaGraph's internal state via the onClick callback.
 */
export function useSegmentClickDetection({ data, layerKeys }: UseSegmentClickDetectionParams) {
  const stackedData = useMemo(() => {
    if (data.length === 0 || layerKeys.length === 0) return null

    const stackGenerator = stack<AreaGraphElement>()
      .offset(stackOffsetDiverging)
      .value((d, key) => d.layerData[key] ?? 0)
      .keys(layerKeys)

    return stackGenerator(data)
  }, [data, layerKeys])

  const resolveClick = useCallback(
    (hourIndex: number, relativeY: number, valueScale: ScaleLinear<number, number>): ClickResult => {
      if (!stackedData || hourIndex < 0 || hourIndex >= data.length) return null

      const clickValue = valueScale.invert(relativeY)

      for (let layerIdx = 0; layerIdx < stackedData.length; layerIdx++) {
        const layer = stackedData[layerIdx]
        const point = layer[hourIndex]
        if (!point) continue

        const y0 = point[0]
        const y1 = point[1]

        const low = Math.min(y0, y1)
        const high = Math.max(y0, y1)

        if (clickValue >= low && clickValue <= high) {
          return { layerKey: layerKeys[layerIdx] }
        }
      }

      return null
    },
    [stackedData, data.length, layerKeys],
  )

  return { resolveClick }
}
